import { useEffect, useRef, useState } from 'react';
import { Button, Empty, Modal, Spin, Tag, Tooltip, message as antdMessage } from 'antd';
import { AudioOutlined, BookOutlined, BulbOutlined, CloseOutlined, CommentOutlined, CompressOutlined, ExpandOutlined, LoadingOutlined, SoundOutlined, StopOutlined } from '@ant-design/icons';
import { io, Socket } from 'socket.io-client';
import MascotFramePlayer from './MascotFramePlayer';
import { mascotAnimations } from './mascotData';
import { useMascotPlayer, type MascotContextState } from './useMascotPlayer';
import api from '../../../../services/api';
import { useAuthStore } from '../../../../stores/authStore';
import type { MascotInterjectionRecord } from '../../index';
import './LiveMeetingCopilotMascot.css';

interface LiveMeetingCopilotMascotProps extends MascotContextState {
  visible?: boolean;
  analyzing?: boolean;
  interjection?: MascotInterjectionRecord | null;
  onInterjectionRespond?: (accept: boolean) => void | Promise<void>;
  meetingTaskId?: string;
  onOpenMeetingTab?: () => void;
  onManualInsight?: () => void;
  /** 产物完成等明确事件的播报：id 变化时播一次 text，杜绝状态推断导致的误报 */
  announcement?: { id: string; text: string } | null;
}

// 分类对齐 docs/plans/ai-demand-center-live-meeting-copilot-plan.md 的8类介入场景；issue/supplement/summary 兼容旧记录
const interjectionCategoryMeta: Record<string, { label: string; color: string }> = {
  business_association: { label: '业务联想', color: 'cyan' },
  point_connection: { label: '关联连接', color: 'geekblue' },
  requirement_clarification: { label: '需求清晰化', color: 'green' },
  concept_correction: { label: '概念纠偏', color: 'volcano' },
  next_thinking_direction: { label: '下一步方向', color: 'blue' },
  missing_dimension: { label: '遗漏提醒', color: 'orange' },
  industry_knowledge: { label: '行业知识', color: 'gold' },
  experience_mapping: { label: '历史经验', color: 'magenta' },
  uncertainty: { label: '答疑解惑', color: 'purple' },
  issue: { label: '发现问题', color: 'volcano' },
  supplement: { label: '补充想法', color: 'geekblue' },
  summary: { label: '阶段小结', color: 'green' },
};

const interjectionStatusMeta: Record<string, { label: string; color: string }> = {
  pending: { label: '还没说', color: 'gold' },
  spoken: { label: '已经说过', color: 'cyan' },
  dismissed: { label: '你跳过了', color: 'default' },
};

function formatInsightTime(value?: string) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' });
}

interface Position {
  x: number;
  y: number;
}

const storageKey = 'adc-live-meeting-mascot-position';

// ── 快速问答录音：Float32 → 16k PCM16，与 AI需求共创 语音输入保持一致 ──
async function float32ToPcm16k(input: Float32Array, inputRate: number) {
  const ratio = inputRate / 16000;
  const length = Math.max(1, Math.floor(input.length / ratio));
  const pcm = new Int16Array(length);
  for (let i = 0; i < length; i += 1) {
    const sample = input[Math.floor(i * ratio)] || 0;
    const clipped = Math.max(-1, Math.min(1, sample));
    pcm[i] = clipped < 0 ? clipped * 0x8000 : clipped * 0x7fff;
  }
  return pcm;
}

function arrayBufferToBase64(buffer: ArrayBuffer) {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return window.btoa(binary);
}

function readInitialPosition(): Position {
  if (typeof window === 'undefined') return { x: 28, y: 142 };
  try {
    const saved = window.localStorage.getItem(storageKey);
    if (saved) {
      const parsed = JSON.parse(saved) as Position;
      if (Number.isFinite(parsed.x) && Number.isFinite(parsed.y)) return parsed;
    }
  } catch {}
  return { x: Math.max(18, window.innerWidth - 248), y: Math.max(98, window.innerHeight - 320) };
}

function clampPosition(pos: Position, minimized: boolean): Position {
  if (typeof window === 'undefined') return pos;
  const width = minimized ? 76 : 220;
  const height = minimized ? 76 : 268;
  return {
    x: Math.min(Math.max(12, pos.x), Math.max(12, window.innerWidth - width - 12)),
    y: Math.min(Math.max(72, pos.y), Math.max(72, window.innerHeight - height - 12)),
  };
}

export default function LiveMeetingCopilotMascot(props: LiveMeetingCopilotMascotProps) {
  const { animation: contextAnimation, animationName: contextAnimationName, message, say } = useMascotPlayer(props);
  const [position, setPosition] = useState<Position>(() => readInitialPosition());
  const [minimized, setMinimized] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [ticklePhase, setTicklePhase] = useState<'idle' | 'anticipation' | 'tickle' | 'recover'>('idle');
  const [tickleNonce, setTickleNonce] = useState(0);
  const dragRef = useRef<{ startX: number; startY: number; origin: Position; dragging: boolean; fromMascotBody: boolean } | null>(null);
  const lastDragAtRef = useRef(0);
  const lastTickleAtRef = useRef(0);
  const tickleTimerRef = useRef<number | null>(null);
  const mascotRootRef = useRef<HTMLDivElement | null>(null);
  const mascotBodyRef = useRef<HTMLDivElement | null>(null);
  const ttsAudioRef = useRef<HTMLAudioElement | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const activeWebAudioRef = useRef<AudioBufferSourceNode | null>(null);  // 互斥：防止多个 WebAudio Source 并发覆盖
  const lastAutoSpeakKeyRef = useRef('');
  const autoSpeakReadyRef = useRef(false);
  // 正在播报的内容标识（null=没在播）：驱动「停止播报」按钮显隐，并让小灵笔记知道正在念哪一条。
  // 只对想法/问答/笔记等长播报置位，点一下小灵的短句不打扰用户
  const [speakingId, setSpeakingId] = useState<string | null>(null);
  const speaking = speakingId !== null;
  // 播报世代号：停止或新播报都会递增，使在途的 TTS 请求返回后自行作废，避免"点了停止又响起来"
  const ttsSeqRef = useRef(0);

  // ── 快速问答状态 ──
  const [qaStatus, setQaStatus] = useState<'idle' | 'listening' | 'thinking' | 'answering'>('idle');
  const [qaBubble, setQaBubble] = useState<string>('');
  const qaSocketRef = useRef<Socket | null>(null);
  const qaAudioCtxRef = useRef<AudioContext | null>(null);
  const qaProcessorRef = useRef<ScriptProcessorNode | null>(null);
  const qaStreamRef = useRef<MediaStream | null>(null);
  const qaFinalRef = useRef('');
  const qaPartialRef = useRef('');
  const qaBubbleTimerRef = useRef<number | null>(null);
  const qaBusyRef = useRef(false);

  // ── 小灵插嘴 & 小灵笔记状态 ──
  const [notesOpen, setNotesOpen] = useState(false);
  const [notesLoading, setNotesLoading] = useState(false);
  const [notes, setNotes] = useState<MascotInterjectionRecord[]>([]);
  const lastInterjectionIdRef = useRef('');

  const isTickling = ticklePhase === 'tickle';
  const animation = isTickling ? mascotAnimations.tickle : contextAnimation;
  const animationName = isTickling ? 'tickle' : contextAnimationName;

  useEffect(() => {
    const next = clampPosition(position, minimized);
    if (next.x !== position.x || next.y !== position.y) setPosition(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minimized]);

  useEffect(() => {
    const handleResize = () => setPosition(prev => clampPosition(prev, minimized));
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [minimized]);

  useEffect(() => {
    try { window.localStorage.setItem(storageKey, JSON.stringify(position)); } catch {}
  }, [position]);

  useEffect(() => {
    if (props.visible && hidden) setHidden(false);
  }, [hidden, props.visible]);


  useEffect(() => {
    return () => {
      if (tickleTimerRef.current) window.clearTimeout(tickleTimerRef.current);
      if (qaBubbleTimerRef.current) window.clearTimeout(qaBubbleTimerRef.current);
      try { qaProcessorRef.current?.disconnect(); } catch {}
      qaStreamRef.current?.getTracks().forEach(track => track.stop());
      qaAudioCtxRef.current?.close().catch(() => undefined);
      try { qaSocketRef.current?.emit('ai-demand:asr-stop'); } catch {}
      qaSocketRef.current?.disconnect();
      // 清理 TTS 音频资源
      try { activeWebAudioRef.current?.stop(); } catch {}
      try { activeWebAudioRef.current?.disconnect(); } catch {}
      try { ttsAudioRef.current?.pause(); } catch {}
    };
  }, []);

  const clearTickleTimer = () => {
    if (tickleTimerRef.current) {
      window.clearTimeout(tickleTimerRef.current);
      tickleTimerRef.current = null;
    }
  };

  const enterTickleRecover = () => {
    clearTickleTimer();
    console.log('[XIAOLING_TICKLE] recover');
    setTicklePhase('recover');
    tickleTimerRef.current = window.setTimeout(() => {
      tickleTimerRef.current = null;
      setTicklePhase('idle');
    }, 190);
  };

  /** 终止所有进行中的 TTS 音频，防止并发播放覆盖；同时作废在途的 TTS 请求 */
  const stopAllTtsAudio = () => {
    ttsSeqRef.current += 1;
    // 终止 WebAudio Source
    if (activeWebAudioRef.current) {
      try { activeWebAudioRef.current.stop(); } catch {}
      try { activeWebAudioRef.current.disconnect(); } catch {}
      activeWebAudioRef.current = null;
    }
    // 终止 HTMLAudio 后备
    if (ttsAudioRef.current) {
      try { ttsAudioRef.current.pause(); } catch {}
      ttsAudioRef.current.currentTime = 0;
      try { ttsAudioRef.current.removeAttribute('src'); } catch {}
      ttsAudioRef.current.load();
      ttsAudioRef.current = null;
    }
    setSpeakingId(null);
  };

  /** 用户主动打断：想法说到一半不想听了，点一下立刻安静 */
  const handleStopSpeaking = () => {
    console.log('[XIAOLING_TTS] stop by user');
    stopAllTtsAudio();
    if (qaStatus === 'answering') setQaStatus('idle');
  };

  const ensureAudioUnlocked = () => {
    try {
      const AudioContextCtor = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioContextCtor) return null;
      if (!audioContextRef.current) audioContextRef.current = new AudioContextCtor();
      const ctx = audioContextRef.current;
      if (ctx.state === 'suspended') void ctx.resume();
      // 静音 unlock buffer 使用 AudioContext 原生采样率，避免重采样噪声
      const nativeRate = ctx.sampleRate || 44100;
      const source = ctx.createBufferSource();
      source.buffer = ctx.createBuffer(1, 1, nativeRate);
      source.connect(ctx.destination);
      source.start(0);
      return ctx;
    } catch (error) {
      console.warn('[XIAOLING_TTS] unlock audio context failed', error);
      return null;
    }
  };

  const resumeAudioContext = async (ctx: AudioContext) => {
    try {
      if (ctx.state === 'suspended') await ctx.resume();
    } catch (error) {
      console.warn('[XIAOLING_TTS] resume audio context failed', error);
    }
  };

  const base64ToArrayBuffer = (base64: string) => {
    // 纯 JS base64 解码 —— 绕过 window.atob 在不同浏览器/编码下偶发抛错的问题
    const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
    const lookup = new Uint8Array(256).fill(255);
    for (let i = 0; i < alphabet.length; i += 1) lookup[alphabet.charCodeAt(i)] = i;

    // 先清除非 base64 字符（空格、换行等），再处理 padding
    let clean = base64.replace(/[^A-Za-z0-9+/=]/g, '');
    let pad = 0;
    while (clean.endsWith('=')) { clean = clean.slice(0, -1); pad += 1; }

    const outLen = Math.floor(clean.length * 3 / 4);
    const buf = new ArrayBuffer(outLen);
    const view = new DataView(buf);
    let srcIdx = 0;
    let dstIdx = 0;

    for (; srcIdx + 3 < clean.length; srcIdx += 4, dstIdx += 3) {
      const a = lookup[clean.charCodeAt(srcIdx)];
      const b = lookup[clean.charCodeAt(srcIdx + 1)];
      const c = lookup[clean.charCodeAt(srcIdx + 2)];
      const d = lookup[clean.charCodeAt(srcIdx + 3)];
      view.setUint8(dstIdx, (a << 2) | (b >> 4));
      view.setUint8(dstIdx + 1, ((b & 0xf) << 4) | (c >> 2));
      view.setUint8(dstIdx + 2, ((c & 0x3) << 6) | d);
    }

    // 处理最后一组（可能有 padding）
    if (srcIdx < clean.length) {
      const a = lookup[clean.charCodeAt(srcIdx)];
      const b = lookup[clean.charCodeAt(srcIdx + 1)] || 0;
      let c = 0, d = 0;
      if (srcIdx + 2 < clean.length) c = lookup[clean.charCodeAt(srcIdx + 2)];
      if (srcIdx + 3 < clean.length) d = lookup[clean.charCodeAt(srcIdx + 3)];
      view.setUint8(dstIdx, (a << 2) | (b >> 4));
      if (dstIdx + 1 < outLen) view.setUint8(dstIdx + 1, ((b & 0xf) << 4) | (c >> 2));
      if (dstIdx + 2 < outLen) view.setUint8(dstIdx + 2, ((c & 0x3) << 6) | d);
    }

    return buf;
  };

  /** speakId：本次播报的标识，小灵笔记用它判断正在念哪一条；默认取 reason */
  const playCuteTts = async (text = '小灵，在呢～', reason = 'manual', speakId = reason) => {
    // 🔇 互斥：当前语音播放中，新语音直接丢弃，不打断不排队
    if (activeWebAudioRef.current || (ttsAudioRef.current && !ttsAudioRef.current.paused)) {
      console.log('[XIAOLING_TTS] skip (playing)', { reason, text, byRef: !!activeWebAudioRef.current ? 'webaudio' : 'htmlaudio' });
      return;
    }

    // 认领世代号：期间用户点了停止播报（或有新播报插入），本次结果作废
    const seq = ttsSeqRef.current + 1;
    ttsSeqRef.current = seq;
    const superseded = () => ttsSeqRef.current !== seq;
    // 点一下小灵、状态切换这类一两秒的短句不弹按钮，免得反复闪
    const stoppable = reason !== 'click' && !reason.startsWith('state-');
    // 取音频要走一趟网络，这期间就把按钮亮出来：用户点「再播报一次」后能立刻反悔，不用干等出声
    if (stoppable) setSpeakingId(speakId);
    const silence = () => { if (!superseded()) setSpeakingId(null); };

    let audioBase64 = '';
    let mime = 'audio/mpeg';
    try {
      const res = await api.post('/ai-demand-center/tts/speak', {
        text,
        speed: 38,
        volume: 80,
        pitch: 55,
      });
      audioBase64 = res.data?.audioBase64 || '';
      mime = res.data?.mime || 'audio/mpeg';
      if (!audioBase64) throw new Error('TTS 接口未返回音频');
      console.log('[XIAOLING_TTS] play', { reason, text, voice: res.data?.voice, audioBase64Length: audioBase64.length });
    } catch (error: any) {
      console.warn('[XIAOLING_TTS] request failed', error?.response?.data?.message || error?.message || error);
      if (!superseded()) say('小灵语音接口没连上～');
      silence();
      return;
    }
    if (superseded()) {
      console.log('[XIAOLING_TTS] drop (stopped while fetching)', { reason, text });
      return;
    }

    const audioContext = audioContextRef.current || ensureAudioUnlocked();
    if (audioContext) {
      try {
        await resumeAudioContext(audioContext);
        const buffer = await audioContext.decodeAudioData(base64ToArrayBuffer(audioBase64).slice(0));
        if (superseded()) {
          console.log('[XIAOLING_TTS] drop (stopped while decoding)', { reason, text });
          return;
        }
        const source = audioContext.createBufferSource();
        source.buffer = buffer;
        source.connect(audioContext.destination);
        // 追踪 active source 以便下次调用时 stop
        activeWebAudioRef.current = source;
        source.onended = () => {
          if (activeWebAudioRef.current !== source) return;
          activeWebAudioRef.current = null;
          silence();
        };
        source.start(0);
        console.log('[XIAOLING_TTS] webaudio started', { state: audioContext.state, duration: buffer.duration });
        return;
      } catch (error: any) {
        console.warn('[XIAOLING_TTS] webaudio failed, fallback to html audio', error?.message || error);
      }
    }

    try {
      if (ttsAudioRef.current) {
        ttsAudioRef.current.pause();
        ttsAudioRef.current.currentTime = 0;
      }
      const audio = new Audio(`data:${mime};base64,${audioBase64}`);
      audio.preload = 'auto';
      audio.onended = () => {
        if (ttsAudioRef.current !== audio) return;
        ttsAudioRef.current = null;
        silence();
      };
      ttsAudioRef.current = audio;
      await audio.play();
      if (superseded()) {
        // 停止播报卡在 play() 的等待期：补一刀，别让它出声
        try { audio.pause(); } catch {}
        if (ttsAudioRef.current === audio) ttsAudioRef.current = null;
        console.log('[XIAOLING_TTS] drop (stopped while starting htmlaudio)', { reason, text });
        return;
      }
      console.log('[XIAOLING_TTS] htmlaudio started');
    } catch (error: any) {
      console.warn('[XIAOLING_TTS] htmlaudio failed', error?.message || error);
      if (!superseded()) say('小灵想说话，声音没打开～');
      silence();
    }
  };

  // ===== 快速问答：语音提问 → 知识库+DeepSeek → 气泡展示 + TTS 播报 =====

  const showQaBubble = (text: string, holdMs = 12000) => {
    setQaBubble(text);
    if (qaBubbleTimerRef.current) window.clearTimeout(qaBubbleTimerRef.current);
    qaBubbleTimerRef.current = window.setTimeout(() => {
      setQaBubble('');
      qaBubbleTimerRef.current = null;
    }, holdMs);
  };

  const cleanupQaAudio = () => {
    try { qaProcessorRef.current?.disconnect(); } catch {}
    qaProcessorRef.current = null;
    qaStreamRef.current?.getTracks().forEach(track => track.stop());
    qaStreamRef.current = null;
    qaAudioCtxRef.current?.close().catch(() => undefined);
    qaAudioCtxRef.current = null;
  };

  const askQuickQa = async (question: string) => {
    const q = (question || '').trim();
    if (!q) {
      setQaStatus('idle');
      showQaBubble('没听清你说的话，再问一次好吗～', 4000);
      return;
    }
    setQaStatus('thinking');
    showQaBubble(`🎤 ${q}`, 30000);
    try {
      const res = await api.post('/ai-demand-center/quick-qa', { question: q });
      const answer = (res.data?.answer || '').trim() || '小灵暂时没能回答这个问题～';
      setQaStatus('answering');
      showQaBubble(answer, 15000);
      console.log('[XIAOLING_QA] answer', { question: q, usedKnowledge: res.data?.usedKnowledge, len: answer.length });
      await playCuteTts(answer, 'quick-qa');
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.message || '快速问答失败';
      console.warn('[XIAOLING_QA] failed', msg);
      showQaBubble(`没答上来：${msg}`, 5000);
    } finally {
      setQaStatus('idle');
    }
  };

  const stopQaRecording = (submit: boolean) => {
    cleanupQaAudio();
    try { qaSocketRef.current?.emit('ai-demand:asr-stop'); } catch {}
    qaSocketRef.current?.disconnect();
    qaSocketRef.current = null;
    const spoken = `${qaFinalRef.current}${qaFinalRef.current && qaPartialRef.current ? ' ' : ''}${qaPartialRef.current}`.trim();
    qaFinalRef.current = '';
    qaPartialRef.current = '';
    if (submit) {
      void askQuickQa(spoken);
    } else {
      setQaStatus('idle');
      setQaBubble('');
    }
  };

  const startQaRecording = async () => {
    if (qaBusyRef.current) return;
    const authUser = useAuthStore.getState().user;
    if (!authUser?.id) {
      antdMessage.warning('请先登录后再使用语音问答');
      return;
    }
    if (!navigator?.mediaDevices?.getUserMedia) {
      antdMessage.error('当前浏览器不支持麦克风，或非 HTTPS 安全上下文');
      return;
    }
    qaBusyRef.current = true;
    ensureAudioUnlocked();
    qaFinalRef.current = '';
    qaPartialRef.current = '';
    setQaStatus('listening');
    showQaBubble('我在听哦，说完再点一下就好～', 60000);
    try {
      const socket = io('/ws', { auth: { token: useAuthStore.getState().token } });
      qaSocketRef.current = socket;
      socket.on('ai-demand:asr-ready', () => console.log('[XIAOLING_QA] asr ready'));
      socket.on('ai-demand:asr-text', (payload: { text?: string; isFinal?: boolean }) => {
        const text = payload?.text?.trim();
        if (!text) return;
        if (payload.isFinal) {
          if (!qaFinalRef.current.endsWith(text)) {
            qaFinalRef.current = `${qaFinalRef.current ? `${qaFinalRef.current} ` : ''}${text}`;
          }
          qaPartialRef.current = '';
        } else {
          qaPartialRef.current = text;
        }
        const live = `${qaFinalRef.current}${qaFinalRef.current && qaPartialRef.current ? ' ' : ''}${qaPartialRef.current}`.trim();
        if (live) showQaBubble(`🎤 ${live}`, 60000);
      });
      socket.on('ai-demand:asr-error', (payload: { message?: string }) => {
        antdMessage.error(payload?.message || '实时转写失败');
        stopQaRecording(false);
      });
      socket.on('connect_error', (err: any) => {
        antdMessage.error(err?.message || '转写服务连接失败');
        stopQaRecording(false);
      });

      socket.emit('ai-demand:asr-start');
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { channelCount: 1, noiseSuppression: true, echoCancellation: true } });
      qaStreamRef.current = stream;
      const AudioContextCtor = window.AudioContext || (window as any).webkitAudioContext;
      const audioContext = new AudioContextCtor();
      qaAudioCtxRef.current = audioContext;
      if (audioContext.state === 'suspended') await audioContext.resume();
      const source = audioContext.createMediaStreamSource(stream);
      const processor = audioContext.createScriptProcessor(4096, 1, 1);
      qaProcessorRef.current = processor;
      processor.onaudioprocess = async event => {
        const socketNow = qaSocketRef.current;
        if (!socketNow?.connected) return;
        const pcm = await float32ToPcm16k(event.inputBuffer.getChannelData(0), audioContext.sampleRate);
        socketNow.emit('ai-demand:asr-audio', { audio: arrayBufferToBase64(pcm.buffer) });
      };
      source.connect(processor);
      processor.connect(audioContext.destination);
    } catch (error: any) {
      cleanupQaAudio();
      try { qaSocketRef.current?.emit('ai-demand:asr-stop'); } catch {}
      qaSocketRef.current?.disconnect();
      qaSocketRef.current = null;
      const msg = error?.name === 'NotAllowedError'
        ? '麦克风权限被拒绝，请在浏览器允许麦克风'
        : (error?.message || '无法启动语音问答');
      antdMessage.error(msg);
      setQaStatus('idle');
      setQaBubble('');
    } finally {
      qaBusyRef.current = false;
    }
  };

  const handleQaButton = () => {
    if (qaStatus === 'listening') {
      stopQaRecording(true);
    } else if (qaStatus === 'idle') {
      void startQaRecording();
    }
    // thinking / answering 状态下按钮禁用，忽略点击
  };

  // ===== 小灵插嘴：新想法到达时先友好询问，确认后才开口 =====

  useEffect(() => {
    const current = props.interjection;
    if (!current?.id || lastInterjectionIdRef.current === current.id) return;
    lastInterjectionIdRef.current = current.id;
    setMinimized(false); // 最小化时收到想法自动展开，避免用户错过询问
    stopAllTtsAudio();
    void playCuteTts('我有一个想法，你要听我说嘛？', 'interject-ask');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.interjection?.id]);

  const handleInterjectionConfirm = () => {
    const current = props.interjection;
    if (!current) return;
    void props.onInterjectionRespond?.(true);
    showQaBubble(`💡 ${current.speech}${current.suggestion ? `\n\n📌 建议：${current.suggestion}` : ''}`, 30000);
    stopAllTtsAudio();
    void playCuteTts(current.speech, 'interject-confirm');
  };

  const handleInterjectionDismiss = () => {
    void props.onInterjectionRespond?.(false);
    showQaBubble('好哒，我先记下来啦～想听的时候点「小灵笔记」就能找到。', 6000);
  };

  // ===== 小灵笔记：查看待说内容与本次会议的插嘴历史 =====

  const fetchNotes = async () => {
    setNotesLoading(true);
    try {
      const res = await api.get('/ai-demand-center/mascot-insights', {
        params: props.meetingTaskId ? { taskId: props.meetingTaskId } : {},
      });
      setNotes(Array.isArray(res.data) ? res.data : []);
    } catch (error: any) {
      antdMessage.error(error?.response?.data?.message || '小灵笔记加载失败');
    } finally {
      setNotesLoading(false);
    }
  };

  const openNotes = () => {
    setNotesOpen(true);
    void fetchNotes();
  };

  const noteSpeakId = (noteId: string) => `note-${noteId}`;

  const speakNote = async (note: MascotInterjectionRecord) => {
    stopAllTtsAudio();
    void playCuteTts(note.speech, 'note-replay', noteSpeakId(note.id));
    if (note.status === 'pending') {
      try {
        await api.patch(`/ai-demand-center/mascot-insights/${note.id}`, { status: 'spoken' });
        setNotes(prev => prev.map(item => (item.id === note.id ? { ...item, status: 'spoken' } : item)));
      } catch (error) {
        console.warn('[XIAOLING_INTERJECT] mark spoken failed', error);
      }
    }
  };

  const renderNoteCard = (note: MascotInterjectionRecord) => {
    const category = interjectionCategoryMeta[note.category] || interjectionCategoryMeta.supplement;
    const status = interjectionStatusMeta[note.status] || interjectionStatusMeta.pending;
    const isSpeakingThis = speakingId === noteSpeakId(note.id);
    return (
      <div key={note.id} className={`adc-mascot-note-card status-${note.status}`}>
        <div className="adc-mascot-note-head">
          <Tag color={category.color}>{category.label}</Tag>
          <span className="adc-mascot-note-title">{note.title}</span>
          <span className="adc-mascot-note-time">{formatInsightTime(note.createdAt)}</span>
          <Tag color={status.color}>{status.label}</Tag>
        </div>
        <div className="adc-mascot-note-speech">{note.speech}</div>
        {note.evidence ? <div className="adc-mascot-note-evidence">🎙 会上原话：“{note.evidence}”</div> : null}
        {note.suggestion ? <div className="adc-mascot-note-suggestion">📌 {note.suggestion}</div> : null}
        <div className="adc-mascot-note-actions">
          {isSpeakingThis ? (
            <Button size="small" danger icon={<StopOutlined />} onClick={handleStopSpeaking}>
              停止播报
            </Button>
          ) : (
            <Button size="small" icon={<SoundOutlined />} onClick={() => void speakNote(note)}>
              {note.status === 'pending' ? '现在听小灵说' : '再听一遍'}
            </Button>
          )}
        </div>
      </div>
    );
  };

  const triggerTickle = (source: string) => {
    // 🎬 互斥：当前正在播放动画（tickle 或语音），新触发直接丢弃
    if (ticklePhase !== 'idle') {
      console.log('[XIAOLING_TICKLE] skip (busy ticklePhase=' + ticklePhase + ')', { source });
      return;
    }
    if (activeWebAudioRef.current || (ttsAudioRef.current && !ttsAudioRef.current.paused)) {
      console.log('[XIAOLING_TICKLE] skip (tts playing)', { source });
      return;
    }
    const now = Date.now();
    if (now - lastTickleAtRef.current < 220) {
      console.log('[XIAOLING_TICKLE] skip duplicate trigger', { source, delta: now - lastTickleAtRef.current });
      return;
    }
    lastTickleAtRef.current = now;
    console.log('[XIAOLING_TICKLE] trigger', { source, animation: mascotAnimations.tickle.name, timestamp: now });
    clearTickleTimer();
    ensureAudioUnlocked();
    setTicklePhase('anticipation');
    say('小灵，在呢～');
    void playCuteTts('小灵，在呢～', 'click');
    tickleTimerRef.current = window.setTimeout(() => {
      tickleTimerRef.current = null;
      setTickleNonce(prev => prev + 1);
      setTicklePhase('tickle');
    }, 190);
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    if (target.closest('button')) return;
    const fromMascotBody = Boolean(target.closest('.adc-live-mascot-body'));
    dragRef.current = { startX: event.clientX, startY: event.clientY, origin: position, dragging: false, fromMascotBody };
    console.log('[XIAOLING_TICKLE] pointerdown', { fromMascotBody, x: event.clientX, y: event.clientY, targetClass: target.className });
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const state = dragRef.current;
    if (!state) return;
    const dx = event.clientX - state.startX;
    const dy = event.clientY - state.startY;
    if (!state.dragging && Math.hypot(dx, dy) <= 8) return;
    state.dragging = true;
    setPosition(clampPosition({ x: state.origin.x + dx, y: state.origin.y + dy }, minimized));
  };

  const handlePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    const state = dragRef.current;
    const wasDragging = Boolean(state?.dragging);
    const fromMascotBody = Boolean(state?.fromMascotBody);
    if (wasDragging) lastDragAtRef.current = Date.now();
    console.log('[XIAOLING_TICKLE] pointerup', { fromMascotBody, wasDragging, x: event.clientX, y: event.clientY });
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {}
    dragRef.current = null;
    if (fromMascotBody && !wasDragging) triggerTickle('pointerup');
  };

  const handleMascotClick = () => {
    const deltaAfterDrag = Date.now() - lastDragAtRef.current;
    console.log('[XIAOLING_TICKLE] click', { deltaAfterDrag });
    if (deltaAfterDrag < 180) return;
    triggerTickle('click');
  };

  useEffect(() => {
    if (!autoSpeakReadyRef.current) {
      autoSpeakReadyRef.current = true;
      lastAutoSpeakKeyRef.current = animationName;
      return;
    }
    // 「文件生成好了」不再靠状态机推断（ready 不代表产物真的生成完），改由 announcement 事件明确播报
    const autoTextByState: Partial<Record<string, string>> = {
      listening: '我在听哦～',
      thinking: '我来整理～',
      knowledge: '我在生成～',
      speaking: '有提醒哦～',
      alert: '注意看哦～',
      error: '出错啦～',
    };
    // 只在动画状态发生切换时播一次；key 里不掺 runningCount 等易变 props，避免同一状态反复突然开口
    if (lastAutoSpeakKeyRef.current === animationName) return;
    lastAutoSpeakKeyRef.current = animationName;
    const text = autoTextByState[animationName];
    if (!text || isTickling) return;
    void playCuteTts(text, `state-${animationName}`);
  }, [animationName, isTickling]);

  // ===== 明确事件播报：产物真正生成完成时由页面推送，内容区分文档/PPT/图片 =====
  const lastAnnouncementIdRef = useRef('');
  useEffect(() => {
    const current = props.announcement;
    if (!current?.id || !current.text || lastAnnouncementIdRef.current === current.id) return;
    lastAnnouncementIdRef.current = current.id;
    say(current.text);
    stopAllTtsAudio();
    void playCuteTts(current.text, 'announcement');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.announcement?.id]);


  useEffect(() => {
    const handleDocumentInteraction = (event: PointerEvent | MouseEvent) => {
      const body = mascotBodyRef.current;
      if (!body || minimized || hidden || notesOpen) return;
      const rect = body.getBoundingClientRect();
      const insideBody = event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
      if (!insideBody) return;
      const target = event.target as HTMLElement | null;
      if (target?.closest?.('button')) return;
      console.log('[XIAOLING_TICKLE] document interaction fallback', { type: event.type, x: event.clientX, y: event.clientY, targetClass: target?.className });
      triggerTickle(`document-${event.type}-fallback`);
    };
    document.addEventListener('pointerup', handleDocumentInteraction, true);
    document.addEventListener('click', handleDocumentInteraction, true);
    return () => {
      document.removeEventListener('pointerup', handleDocumentInteraction, true);
      document.removeEventListener('click', handleDocumentInteraction, true);
    };
  }, [hidden, minimized, notesOpen]);

  if (!props.visible && hidden) {
    return (
      <button type="button" className="adc-mascot-restore" onClick={() => setHidden(false)}>
        小助手
      </button>
    );
  }

  if (!props.visible && !hidden) return null;

  return (
    <div
      ref={mascotRootRef}
      className={`adc-live-mascot ${minimized ? 'is-minimized' : ''} state-${animationName} tickle-phase-${ticklePhase}`}
      style={{ transform: `translate3d(${position.x}px, ${position.y}px, 0)` }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
    >
      <div className="adc-live-mascot-toolbar">
        <span className="adc-live-mascot-status" />
        <span>{animation.label}</span>
        {speaking && (
          <Tooltip title="停止播报">
            <Button type="text" size="small" danger icon={<StopOutlined />} onClick={handleStopSpeaking} />
          </Tooltip>
        )}
        <Tooltip title={minimized ? '展开' : '最小化'}>
          <Button type="text" size="small" icon={minimized ? <ExpandOutlined /> : <CompressOutlined />} onClick={() => setMinimized(v => !v)} />
        </Tooltip>
        <Tooltip title="关闭浮窗">
          <Button type="text" size="small" icon={<CloseOutlined />} onClick={() => setHidden(true)} />
        </Tooltip>
      </div>

      {!minimized && (
        <div className="adc-live-mascot-bubble">
          <strong>智坊小灵</strong>
          <span>{message}</span>
        </div>
      )}

      {qaBubble && (
        <div className={`adc-live-mascot-qa-bubble qa-${qaStatus}`}>
          <span>{qaBubble}</span>
        </div>
      )}

      {speaking && !minimized && (
        <div className="adc-live-mascot-stop">
          <Button size="small" danger block icon={<StopOutlined />} onClick={handleStopSpeaking}>
            停止播报
          </Button>
        </div>
      )}

      {props.interjection && !minimized && (
        <div className={`adc-live-mascot-interject category-${props.interjection.category}`}>
          <div className="adc-live-mascot-interject-head">
            <BulbOutlined />
            <span>我有一个想法，你要听我说嘛？</span>
          </div>
          <div className="adc-live-mascot-interject-title">
            <Tag color={(interjectionCategoryMeta[props.interjection.category] || interjectionCategoryMeta.supplement).color}>
              {(interjectionCategoryMeta[props.interjection.category] || interjectionCategoryMeta.supplement).label}
            </Tag>
            <span>{props.interjection.title}</span>
          </div>
          <div className="adc-live-mascot-interject-actions">
            <Button size="small" type="primary" onClick={handleInterjectionConfirm}>好，你说</Button>
            <Button size="small" onClick={handleInterjectionDismiss}>先不用</Button>
          </div>
        </div>
      )}

      <div
        ref={mascotBodyRef}
        className="adc-live-mascot-body"
        onClick={handleMascotClick}
        onDoubleClick={() => say('我可以拖到屏幕任意位置，需要时会结合当前页面帮你提醒和带路。')}
        title="点一下小灵"
      >
        <MascotFramePlayer
          key={`${animation.name}-${isTickling ? tickleNonce : 0}`}
          animation={animation}
          onComplete={isTickling ? enterTickleRecover : undefined}
        />
      </div>

      {!minimized && (
        <div className="adc-live-mascot-actions">
          <Button
            size="small"
            type={qaStatus === 'listening' ? 'primary' : 'default'}
            danger={qaStatus === 'listening'}
            icon={qaStatus === 'thinking' || qaStatus === 'answering' ? <LoadingOutlined /> : <AudioOutlined spin={qaStatus === 'listening'} />}
            disabled={qaStatus === 'thinking' || qaStatus === 'answering'}
            onClick={handleQaButton}
          >
            {qaStatus === 'listening' ? '说完了' : qaStatus === 'thinking' ? '思考中' : qaStatus === 'answering' ? '回答中' : '问小灵'}
          </Button>
          <Button size="small" icon={<CommentOutlined />} loading={props.analyzing} onClick={props.onManualInsight}>手动提醒</Button>
          <Button size="small" type="primary" onClick={props.onOpenMeetingTab}>去录音</Button>
          <Button size="small" icon={<BookOutlined />} onClick={openNotes}>小灵笔记</Button>
        </div>
      )}

      <Modal
        open={notesOpen}
        onCancel={() => setNotesOpen(false)}
        footer={null}
        width={580}
        zIndex={2300}
        className="adc-mascot-notes-modal"
        title={(
          <div className="adc-mascot-notes-title">
            <BulbOutlined />
            <span>小灵笔记</span>
            <em>{props.meetingTaskId ? '本次会议小灵想说的和已经说过的都在这里' : '小灵最近想说的和已经说过的都在这里'}</em>
            {/* 念想法/念回答时打开笔记：浮窗上的停止按钮被弹窗盖住了，这里补一个 */}
            {speaking && !speakingId?.startsWith('note-') && (
              <Button size="small" danger icon={<StopOutlined />} onClick={handleStopSpeaking}>
                停止播报
              </Button>
            )}
          </div>
        )}
      >
        <Spin spinning={notesLoading}>
          {(() => {
            const pendingNotes = notes.filter(note => note.status === 'pending');
            const historyNotes = notes.filter(note => note.status !== 'pending');
            if (!notes.length) {
              return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="小灵还没有想说的内容，会议进行中它会持续思考～" />;
            }
            return (
              <div className="adc-mascot-notes-body">
                <div className="adc-mascot-notes-section">
                  <div className="adc-mascot-notes-section-title">
                    💡 想说还没说 <i>{pendingNotes.length}</i>
                  </div>
                  {pendingNotes.length
                    ? pendingNotes.map(renderNoteCard)
                    : <div className="adc-mascot-notes-empty">暂时没有攒着的想法～</div>}
                </div>
                <div className="adc-mascot-notes-section">
                  <div className="adc-mascot-notes-section-title">
                    🗂 说过的历史 <i>{historyNotes.length}</i>
                  </div>
                  {historyNotes.length
                    ? historyNotes.map(renderNoteCard)
                    : <div className="adc-mascot-notes-empty">本次会议小灵还没开过口～</div>}
                </div>
              </div>
            );
          })()}
        </Spin>
      </Modal>
    </div>
  );
}
